import { useMutation } from '@apollo/client';
import type {
  DocumentNode,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';

import { monitoringClient } from '@adminhub/monitoring-client';

import { useExperienceTracker } from './use-experience-tracker';

interface ExperienceTrackingMutationOptions {
  id: string;
  name: string;
  attributes?: object;
}

const useMutationAdminhub = <TData, TVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables> & { experienceTracking?: ExperienceTrackingMutationOptions },
): MutationTuple<TData, TVariables> => {
  const [mutationFn, ...rest] = useMutation<TData, TVariables>(mutation, options);

  const experienceTracker = useExperienceTracker();
  const experienceTrackingOptions = options?.experienceTracking;
  const hasExperienceTracking = !!experienceTrackingOptions;

  const handleErrorsMutation = async (mutationArgs?: MutationFunctionOptions<TData, TVariables>) => {
    try {
      if (hasExperienceTracking) {
        experienceTracker.start({
          id: experienceTrackingOptions.id,
          name: experienceTrackingOptions.name,
          attributes: experienceTrackingOptions.attributes,
        });
      }

      const response = await mutationFn(mutationArgs);
      // Experience tracker success based on options
      if (hasExperienceTracking) {
        experienceTracker.succeed({
          name: experienceTrackingOptions.name,
          attributes: experienceTrackingOptions.attributes,
        });
      }

      return response;
    } catch (error) {
      // fire monitoring client errors based on team
      monitoringClient.onError(error);

      // experience tracker fail based on options
      if (hasExperienceTracking) {
        experienceTracker.stopOnError({
          name: experienceTrackingOptions.name,
          error,
        });
      }

      throw error;
    }
  };

  return [handleErrorsMutation, ...rest];
};
// TODO: This will eventually become export { useMutationAdminhub as useMutation }. We are doing it
// this way so that we can test one or two areas first before the general use.
export { useMutationAdminhub };
